import 'core-js/stable'
import 'regenerator-runtime/runtime'
import 'focus-visible'
import 'whatwg-fetch'

// vfile@2 (pulled in by react-markdown@4) calls process.cwd() in its constructor,
// which doesn't exist in the browser. Webpack auto-polyfilled this; Vite does not,
// so markdown rendering (e.g. the AI estimate assistant) would crash without it.
const _process = ((globalThis as any).process = (globalThis as any).process || {})
if (typeof _process.cwd !== 'function') {
  _process.cwd = () => '/'
}
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  let updateDispatched = false

  const dispatchUpdateReady = (source: string) => {
    if (updateDispatched) return
    updateDispatched = true
    console.log(`[UpdateChecker] New version available (${source}), dispatching UPDATE_READY`)
    window.dispatchEvent(new Event('UPDATE_READY'))
    window.localStorage.setItem('lastUpdateCheck', JSON.stringify(Date.now()))
    // purge after update in case of any changes to the apollo cache
    window.localStorage.removeItem('lastPurge')
  }

  // With registerType: 'autoUpdate', the workbox SW calls skipWaiting() automatically,
  // so the new SW activates immediately. The most reliable signal is controllerchange,
  // which fires when a new SW takes over the page.
  let initialController = navigator.serviceWorker.controller
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Only treat as an update if we already had a controller (not first install)
    if (initialController) {
      dispatchUpdateReady('controllerchange')
    }
    initialController = navigator.serviceWorker.controller
  })

  // Also listen for the waiting state in case skipWaiting is not immediate
  navigator.serviceWorker.ready.then((registration) => {
    if (registration.waiting) {
      console.log('[UpdateChecker] Found waiting service worker on load')
      dispatchUpdateReady('waiting on load')
    }

    registration.addEventListener('updatefound', () => {
      const newSW = registration.installing
      if (!newSW) return
      console.log('[UpdateChecker] Update found, new service worker installing...')
      newSW.addEventListener('statechange', () => {
        if (newSW.state === 'installed') {
          console.log('[UpdateChecker] New service worker reached installed state')
          dispatchUpdateReady('installed')
        }
      })
    })
  })
}

// suppress react warnings we dont care about in development
if (process.env.NODE_ENV === 'development') {
  const errFn = console.error
  const ignore = [
    // Annoying material-ui errors (which are warnings) when a classes prop has keys that aren't used by that component
    // (we do this a lot intentionally, and there's no need to manually pull out class names for sake of the error)
    /provided to the classes prop is not implemented/,
    // thrown when a DOM element receives an unknown prop. this happens a lot with ...props, and is rarely consequential
    /Warning: React does not recognize the/
  ]
  console.error = (msg: string, ...args: string[]) => {
    if (ignore.every((re) => !msg?.match?.(re))) {
      errFn.call(console, msg, ...args)
    }
  }
}

import React from 'react'
import { createRoot } from 'react-dom/client'
import { PostHogProvider } from '@posthog/react'
import { App } from './components/App'
import './index.css'

const posthogOptions = {
  api_host: process.env.POSTHOG_HOST,
  defaults: '2026-01-30',
  disable_session_recording: true,
  // Disable all automatic event capture to control PostHog event volume/cost.
  // We rely solely on explicit track() calls. Overrides the `defaults` preset,
  // which would otherwise enable autocapture and automatic $pageview/$pageleave.
  // cspell:disable-next-line
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false
} as const

const render = (_Component: React.ComponentType) => {
  const container = document.getElementById('root')
  const root = createRoot(container)
  root.render(
    <PostHogProvider apiKey={process.env.POSTHOG_PROJECT_TOKEN} options={posthogOptions}>
      <App />
    </PostHogProvider>
  )
}

render(App)
